import { useMemo, useState } from "react";
import "./PieChart.css";
import { formatUsdAmountDisplayCents } from "../../shared/utils/function";

const MIN_CHART_PERCENTAGE = 0.5;

const getAmountBasedPercentages = (data) => {
    const totalAmount = data.reduce((sum, item) => sum + (item.amount || 0), 0);
    if (totalAmount <= 0) {
        return data.map((item) => item.percentage || 0);
    }

    const rawPercentages = data.map((item) => {
        const amount = item.amount || 0;
        return amount > 0 ? (amount / totalAmount) * 100 : 0;
    });

    let boostNeeded = 0;
    rawPercentages.forEach((pct, index) => {
        const amount = data[index].amount || 0;
        if (amount > 0 && pct > 0 && pct < MIN_CHART_PERCENTAGE) {
            boostNeeded += MIN_CHART_PERCENTAGE - pct;
        }
    });

    if (boostNeeded === 0) {
        return rawPercentages;
    }

    const largeTotal = rawPercentages.reduce((sum, pct, index) => {
        const amount = data[index].amount || 0;
        return amount > 0 && pct >= MIN_CHART_PERCENTAGE ? sum + pct : sum;
    }, 0);

    return rawPercentages.map((pct, index) => {
        const amount = data[index].amount || 0;
        if (amount > 0 && pct > 0 && pct < MIN_CHART_PERCENTAGE) {
            return MIN_CHART_PERCENTAGE;
        }
        if (largeTotal > 0 && amount > 0 && pct >= MIN_CHART_PERCENTAGE) {
            return Math.max(0, pct - (pct / largeTotal) * boostNeeded);
        }
        return pct;
    });
};

const PieChart = ({ data, size = 200, strokeWidth = 30 }) => {
    const [hoveredSegment, setHoveredSegment] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    
    const radius = (size - strokeWidth) / 2;
    const center = size / 2;
    const circumference = 2 * Math.PI * radius;

    const totalAmount = useMemo(
        () => data.reduce((sum, item) => sum + (item.amount || 0), 0),
        [data]
    );

    const chartPercentages = useMemo(
        () => getAmountBasedPercentages(data),
        [data]
    );

    const segments = useMemo(() => {
        let cumulativePercentage = 0;
        return data.map((item, index) => {
            const chartPercentage = chartPercentages[index] || 0;
            const strokeDasharray = `${(chartPercentage / 100) * circumference} ${circumference}`;
            const strokeDashoffset = -(cumulativePercentage / 100) * circumference;
            const displayPercentage = totalAmount > 0 && (item.amount || 0) > 0
                ? ((item.amount || 0) / totalAmount) * 100
                : (item.percentage || 0);

            const segment = {
                ...item,
                chartPercentage,
                displayPercentage,
                strokeDasharray,
                strokeDashoffset,
            };

            cumulativePercentage += chartPercentage;
            return segment;
        });
    }, [data, chartPercentages, circumference, totalAmount]);

    // Check if all values are 0
    const hasData = useMemo(() => {
        return data.some(item => (item.percentage || 0) > 0 || (item.amount || 0) > 0);
    }, [data]);

    const handleMouseEnter = (segment, event) => {
        setHoveredSegment(segment);
        const rect = event.currentTarget.closest('.pie-chart-container').getBoundingClientRect();
        setTooltipPosition({
            x: event.clientX - rect.left,
            y: event.clientY - rect.top
        });
    };

    const handleMouseMove = (event) => {
        if (hoveredSegment) {
            const rect = event.currentTarget.closest('.pie-chart-container').getBoundingClientRect();
            setTooltipPosition({
                x: event.clientX - rect.left,
                y: event.clientY - rect.top
            });
        }
    };

    const handleMouseLeave = () => {
        setHoveredSegment(null);
    };

    return (
        <div 
            className="pie-chart-container"
            onMouseMove={handleMouseMove}
        >
            <svg width={size} height={size} className="pie-chart-svg">
                {hasData ? (
                    segments.map((segment, index) => {
                        if ((segment.amount || 0) <= 0 && segment.chartPercentage <= 0) return null;

                        return (
                            <circle
                                key={index}
                                cx={center}
                                cy={center}
                                r={radius}
                                fill="none"
                                stroke={segment.color}
                                strokeWidth={strokeWidth}
                                strokeDasharray={segment.strokeDasharray}
                                strokeDashoffset={segment.strokeDashoffset}
                                strokeLinecap="butt"
                                className="pie-chart-segment"
                                onMouseEnter={(e) => handleMouseEnter(segment, e)}
                                onMouseLeave={handleMouseLeave}
                                style={{
                                    cursor: 'pointer',
                                    opacity: hoveredSegment && hoveredSegment !== segment ? 0.5 : 1,
                                    transition: 'opacity 0.2s ease'
                                }}
                            />
                        );
                    })
                ) : (
                    // Show empty circle when all values are 0 - full circle with same dimensions
                    <circle
                        cx={center}
                        cy={center}
                        r={radius}
                        fill="none"
                        stroke="#6B7280"
                        strokeWidth={strokeWidth}
                        strokeDasharray={`${circumference} 0`}
                        strokeDashoffset={0}
                        strokeLinecap="round"
                        className="pie-chart-segment"
                        style={{
                            opacity: 0.3
                        }}
                    />
                )}
            </svg>

            {hoveredSegment && (
                <div 
                    className="pie-chart-tooltip"
                    style={{
                        left: `${tooltipPosition.x}px`,
                        top: `${tooltipPosition.y}px`
                    }}
                >
                    <div className="tooltip-label">{hoveredSegment.label}</div>
                    {hoveredSegment.amount !== undefined && (
                        <div className="tooltip-value">${formatUsdAmountDisplayCents(hoveredSegment.amount)}</div>
                    )}
                    <div className="tooltip-percentage">{hoveredSegment.displayPercentage.toFixed(1)}%</div>
                </div>
            )}
        </div>
    );
};

export default PieChart;

